import React, { useState, useEffect, Fragment } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, getDocs, query, orderBy, deleteDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Dialog, Transition } from '@headlessui/react';
import { BIBLE_BOOKS } from '../utils/bibleData';
import { PencilSquareIcon, MicrophoneIcon } from '@heroicons/react/24/outline';
import StudyNoteEditor from './StudyNoteEditor';

const StudyNotesManager = ({ 
  isOpen, 
  onClose, 
  currentUser
}) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [bookFilter, setBookFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'written', 'audio'
  const [editingNote, setEditingNote] = useState(null);
  const [showNoteEditor, setShowNoteEditor] = useState(false);

  // Fetch the current user's own notes from their personal collection
  const fetchUserNotes = async () => {
    if (!currentUser?.uid) {
      setNotes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const notesQuery = query(
        collection(db, 'users', currentUser.uid, 'notes'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(notesQuery);

      const allNotes = snapshot.docs
        .filter(doc => doc.id !== '_init' && doc.data().noteType)
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

      setNotes(allNotes);
    } catch (error) {
      console.error('❌ StudyNotesManager: Error fetching user notes:', error);
      setError('Failed to load notes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch notes when drawer opens
  useEffect(() => {
    if (isOpen) {
      fetchUserNotes();
    }
  }, [isOpen, currentUser]);

  // Filter notes based on search and filters
  const filteredNotes = notes.filter(note => {
    // Search filter
    if (searchTerm && !note.content.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Book filter
    if (bookFilter && note.book !== bookFilter) {
      return false;
    }

    // Type filter
    if (typeFilter !== 'all' && note.noteType !== typeFilter) {
      return false;
    }

    return true;
  });

  // Handle note deletion
  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'notes', noteId));
      setNotes(notes.filter(note => note.id !== noteId));
    } catch (error) {
      console.error('❌ Error deleting note:', error);
      setError('Failed to delete note. Please try again.');
    }
  };

  // Handle sharing note: mark it shareable, then copy a public link that
  // includes the owner uid so ShareableNoteView can locate it.
  const handleShareNote = async (noteId) => {
    try {
      await updateDoc(doc(db, 'users', currentUser.uid, 'notes', noteId), { isShared: true });
      setNotes(prev => prev.map(n => (n.id === noteId ? { ...n, isShared: true } : n)));

      const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
      const shareUrl = `${baseUrl}/notes/${currentUser.uid}/${noteId}`;
      await navigator.clipboard.writeText(shareUrl);
      alert('Share link copied to clipboard!');
    } catch (error) {
      console.error('❌ Error copying share link:', error);
      setError('Failed to copy share link. Please try again.');
    }
  };

  // Handle note editing
  const handleEditNote = (note) => {
    setEditingNote(note);
    setShowNoteEditor(true);
  };

  // Handle note save (for editing)
  const handleNoteSave = () => {
    fetchUserNotes(); // Refresh notes
    setEditingNote(null);
    setShowNoteEditor(false);
  };

  // Get book name from ID
  const getBookName = (bookId) => {
    const book = BIBLE_BOOKS.find(b => b.id === bookId);
    return book ? book.name : bookId;
  };

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    const now = new Date();
    const noteTime = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffMs = now - noteTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return noteTime.toLocaleDateString();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300 sm:duration-500"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300 sm:duration-500"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="w-screen max-w-2xl">
                  <div className="h-full flex flex-col bg-white shadow-xl py-6 px-6">
                    <div className="flex items-center justify-between mb-4">
                      <Dialog.Title className="text-lg font-medium text-gray-900">
                        My Study Notes ({notes.length})
                      </Dialog.Title>
                      <button
                        className="text-gray-400 hover:text-gray-600"
                        onClick={onClose}
                      >
                        <span className="sr-only">Close panel</span>
                        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="mb-4 space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search Notes</label>
                        <input
                          type="text"
                          className="input-field"
                          placeholder="Search note content..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Book</label>
                          <select
                            className="input-field"
                            value={bookFilter}
                            onChange={(e) => setBookFilter(e.target.value)}
                          >
                            <option value="">All Books</option>
                            {BIBLE_BOOKS.map(book => (
                              <option key={book.id} value={book.id}>{book.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                          <select
                            className="input-field"
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                          >
                            <option value="all">All Types</option>
                            <option value="written">Written</option>
                            <option value="audio">Audio</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4 flex-1 overflow-y-auto">
                      {/* Add Note Button */}
                      <button
                        onClick={() => setShowNoteEditor(true)}
                        className="w-full mb-4 p-3 bg-cerulean text-white rounded-lg hover:bg-space-cadet transition-colors font-medium flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add New Note
                      </button>

                      {/* Error Message */}
                      {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
                          {error}
                        </div>
                      )}

                      {/* Loading State */}
                      {loading && (
                        <div className="text-center py-8">
                          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-cerulean"></div>
                          <p className="text-gray-600 mt-2">Loading notes...</p>
                        </div>
                      )}

                      {/* Notes List */}
                      {!loading && filteredNotes.length === 0 ? (
                        <div className="text-center py-8">
                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </div>
                          <p className="text-gray-600">
                            {searchTerm || bookFilter || typeFilter !== 'all' 
                              ? 'No notes match your filters' 
                              : 'No notes yet'
                            }
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            {searchTerm || bookFilter || typeFilter !== 'all'
                              ? 'Try adjusting your search or filters'
                              : 'Start adding notes to build your study collection'
                            }
                          </p>
                        </div>
                      ) : (
                        filteredNotes.map(note => (
                          <div key={note.id} className="bg-gray-50 p-4 rounded-lg">
                            {/* Note Header */}
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-space-cadet">
                                  {getBookName(note.book)} {note.chapter}
                                  {note.verse && `:${note.verse}`}
                                  {note.verseRange && `:${note.verseRange.start}-${note.verseRange.end}`}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {formatRelativeTime(note.createdAt)}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleShareNote(note.id)}
                                  className="p-1 text-gray-400 hover:text-aquamarine transition-colors"
                                  title="Share note"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleEditNote(note)}
                                  className="p-1 text-gray-400 hover:text-cerulean transition-colors"
                                  title="Edit note"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDeleteNote(note.id)}
                                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                  title="Delete note"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>

                            {/* Note Type Badge */}
                            <div className="mb-2">
                              <span className={`text-xs px-2 py-1 rounded-full inline-flex items-center gap-1 ${
                                note.noteType === 'written'
                                  ? 'bg-cerulean bg-opacity-10 text-cerulean'
                                  : 'bg-coral bg-opacity-10 text-coral'
                              }`}>
                                {note.noteType === 'written'
                                  ? <><PencilSquareIcon className="w-3.5 h-3.5" /> Written</>
                                  : <><MicrophoneIcon className="w-3.5 h-3.5" /> Audio</>}
                              </span>
                            </div>

                            {/* Note Content */}
                            <div className="text-gray-800">
                              {note.noteType === 'written' ? (
                                <p className="text-sm leading-relaxed">{note.content}</p>
                              ) : (
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
                                    </svg>
                                    Audio note ({note.audioDuration ? `${Math.round(note.audioDuration)}s` : 'Unknown duration'})
                                  </div>
                                  {note.audioUrl && (
                                    <audio controls className="w-full" playsInline>
                                      <source src={note.audioUrl} type={note.audioType || 'audio/mp4'} />
                                      Your browser does not support the audio element.
                                    </audio>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}

                      {/* Results Count */}
                      {!loading && filteredNotes.length > 0 && (
                        <div className="text-center text-sm text-gray-500 mt-4">
                          Showing {filteredNotes.length} of {notes.length} notes
                        </div>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Study Note Editor Modal */}
      <StudyNoteEditor
        isOpen={showNoteEditor}
        onClose={() => {
          setShowNoteEditor(false);
          setEditingNote(null);
        }}
        note={editingNote}
        onSave={handleNoteSave}
      />
    </Transition.Root>
  );
};

export default StudyNotesManager;
